import React from 'react';
import { Icon } from '@/shared/icons';

/**
 * Paginação numerada das listagens — `.pager` do DS (mockup crm-customers.html):
 * esquerda "Mostrando X–Y de N {itens}" (12px muted) · direita ‹ 1 2 3 … N ›
 * (`.pg-btn` 30px r7 borda strong; ativa = lilás `--active-bg` + texto roxo).
 *
 * @example
 * <ListPagePagination
 *   currentPage={page}
 *   totalPages={totalPages}
 *   onPageChange={setPage}
 *   totalItems={data.total}
 *   pageSize={20}
 *   itemLabel="clientes"
 * />
 */
export interface ListPagePaginationProps {
  /** Current page number (1-based) */
  currentPage: number;
  /** Total number of pages */
  totalPages: number;
  /** Callback when page changes */
  onPageChange: (page: number) => void;
  /** Show page info text */
  showPageInfo?: boolean;
  /** Total de itens (habilita "Mostrando X–Y de N") */
  totalItems?: number;
  /** Itens por página (para calcular X–Y; default 20) */
  pageSize?: number;
  /** Substantivo do contador (ex: "clientes", "leads"); default "itens" */
  itemLabel?: string;
}

/** Janela de páginas: 1 … (p-1) p (p+1) … N — máx 7 slots como no mockup. */
const pageWindow = (current: number, total: number): (number | '…')[] => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | '…')[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) pages.push('…');
  for (let p = start; p <= end; p++) pages.push(p);
  if (end < total - 1) pages.push('…');
  pages.push(total);
  return pages;
};

export const ListPagePagination: React.FC<ListPagePaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  showPageInfo = true,
  totalItems,
  pageSize = 20,
  itemLabel = 'itens',
}) => {
  // Com `totalItems` o pager fica SEMPRE visível ("Mostrando X–Y de N" — padrão dos
  // mockups); sem totalItems, mantém o comportamento legado (some com 1 página).
  if (totalPages <= 1 && totalItems == null) {
    return null;
  }
  if (totalItems === 0) {
    return null;
  }

  const from = (currentPage - 1) * pageSize + 1;
  const to = totalItems != null ? Math.min(currentPage * pageSize, totalItems) : currentPage * pageSize;

  const btnStyle = (active: boolean): React.CSSProperties => ({
    minWidth: 30,
    height: 30,
    padding: '0 8px',
    borderRadius: 7,
    fontSize: 12,
    fontWeight: 600,
    border: active ? '1px solid transparent' : '1px solid var(--theme-input-border)',
    backgroundColor: active ? 'var(--theme-primary-background)' : 'var(--theme-card-background)',
    color: active ? 'var(--theme-primary)' : 'var(--theme-card-text)',
  });

  return (
    <div
      className="flex items-center justify-between gap-3 mt-6 flex-wrap"
      style={{ padding: '12px 4px 2px' }}
      data-testid="list-pagination"
    >
      {/* "Mostrando X–Y de N" (quando totalItems fornecido) ou "Página X de Y" */}
      {showPageInfo && (
        <span className="text-xs select-none" style={{ color: 'var(--theme-muted-text)' }}>
          {totalItems != null
            ? `Mostrando ${from}–${to} de ${totalItems} ${itemLabel}`
            : `Página ${currentPage} de ${totalPages}`}
        </span>
      )}

      <div className="flex items-center gap-1 ml-auto">
        <button
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="inline-flex items-center justify-center disabled:opacity-40 transition-colors"
          style={btnStyle(false)}
          aria-label="Página anterior"
          data-testid="list-pagination-prev"
        >
          <Icon name="chevron-left" className="w-3.5 h-3.5" />
        </button>

        {pageWindow(currentPage, totalPages).map((p, i) =>
          p === '…' ? (
            <span key={`e${i}`} className="px-1 text-xs select-none" style={{ color: 'var(--theme-muted-text)' }}>
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onPageChange(p)}
              className="inline-flex items-center justify-center transition-colors tabular-nums"
              style={btnStyle(p === currentPage)}
              aria-current={p === currentPage ? 'page' : undefined}
              data-testid={`list-pagination-page-${p}`}
            >
              {p}
            </button>
          )
        )}

        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="inline-flex items-center justify-center disabled:opacity-40 transition-colors"
          style={btnStyle(false)}
          aria-label="Próxima página"
          data-testid="list-pagination-next"
        >
          <Icon name="chevron-right" className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

ListPagePagination.displayName = 'ListPagePagination';
